import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import useCompanyLicenseEntitlements from '../api/useCompanyLicenseEntitlements';
import usePermissions from '../auth/usePermissions';
import useMayPurchaseLicense from './useMayPurchaseLicense';
import {
  LicenseErrorCode,
  PAYWALL_CARD_CANCELLED_PARAM,
  PAYWALL_CARD_RETURN_PARAM,
  holdsLicense,
} from '../../services/tenant/providerBilling';
import { PROVIDER_CONTACT_TOAST_ID, isProviderContactRequired } from '../../services/helpers/ProviderContactError';
import { PaywallOutcome, createPaywallView, paywallBlocker } from '../../services/analytics/paywallEvents';

/** The saved card arrives by webhook, which can lag Stripe's redirect back. */
const CARD_POLL_ATTEMPTS = 6;
const CARD_POLL_INTERVAL_MS = 2000;

/**
 * One licence gate per trigger screen (PROD-494).
 *
 * Every value moment does the same six things: read the entitlements, decide
 * whether the paywall must open, hold the action the partner was about to
 * take, buy the product, run that action once bought, and hand the modal its
 * props. Four screens had their own copy of that and had already drifted —
 * one forgot to persist the action, one reported success on failure. The
 * orchestration lives here once; a screen only says what its action is.
 *
 *   const gate = useLicenseGate(company.id, LicenseProductKey.PartnerPlan, { company });
 *   onClick={() => gate.gate(activateTheThing)}         // runs directly when licensed
 *   onClick={() => gate.gate(goLive, { always: true })}  // Go Live always confirms
 *   <LicensePaywallModal {...gate.modalProps} />
 *
 * `proceed` may return `{ ok, error }`; a failure is surfaced inside the modal
 * and the action stays held, so a retry runs it again rather than declaring
 * a purchase that did nothing a success.
 *
 * Only a partner admin (and a super admin acting for them) may buy — the same
 * rule the activate route enforces. For anyone else the paywall is not active
 * at all (see useCompanyLicenseEntitlements): the modal never opens and the
 * action runs as it is. The backend refuses it for a PLG partner's restaurant
 * user with `provider_contact_required`, which the API client already toasts
 * with the partner's contact; a bare `license_required` (another partner's
 * restaurant user) gets a toast here. Never a price, a trial or a plan name —
 * the partner resells the licence at its own price.
 *
 * A card on file comes before any purchase (product decision 2026-09-22): the
 * backend refuses to activate, and to launch, without one. The modal is told
 * so through `requiresPaymentMethod` and sends the partner through Stripe
 * Checkout; on the way back this hook waits for the method to land and
 * reopens the paywall, so the partner picks up where they left off. Only an
 * actual purchase needs one — confirming a plan the restaurant already holds
 * does not, unless the screen's action is itself refused without one: Go Live
 * passes `paymentMethodToConfirm`, because the launch checklist demands the
 * method whether or not anything is bought. A super admin cannot enter the
 * partner's details, so the step never blocks them — the backend lets them
 * through and go-live keeps the gate they may waive.
 *
 * `resumeAfterCardReturn` is what the reopened paywall runs after the purchase
 * on the way back from Stripe, since the action held at the click does not
 * survive the redirect. It is read on each return, so a screen may pass the
 * function it would have gated, closing over its latest state.
 *
 * One `gate()` call at a time per screen: a second click while the first is
 * still deciding (waiting for the entitlements, or running the action) is
 * dropped, so a double-click cannot launch the restaurant or switch live
 * orders on twice. `pending` is true for that window; the trigger shows it.
 *
 * The returned object and every function on it are stable between renders
 * (the repo rule for shared hooks).
 *
 * `paywallGate` names the value moment for analytics (PaywallGate): each time the
 * paywall is shown it sends paywall_viewed, and exactly one paywall_outcome when it
 * ends — subscribed, blocked_no_payment_method, blocked_no_base or dismissed — and
 * it rides along with the activation as `gate`. A confirmation of a licence the
 * restaurant already holds, with nothing else asked, is not a paywall and reports
 * nothing. A screen whose own subscribe step launches the restaurant reports how
 * that went through `reportPaywallSubscribed` / `reportPaywallRefusal`, and
 * `handOffPaywall` closes the modal while the purchase continues elsewhere (the
 * review drawer, "Go live anyway") so its outcome is the launch's, not a dismissal.
 */
export default function useLicenseGate(
  companyId,
  productKey,
  { company = null, paymentMethodToConfirm = false, paywallGate = null, resumeAfterCardReturn = null } = {},
) {
  const licensing = useCompanyLicenseEntitlements(companyId);
  const { paywallActive, refresh, activate, entitlements, error, createCardCheckout } = licensing;
  const { t } = useTranslation(undefined, { keyPrefix: 'Hooks:useLicenseGate' });
  const { isSuperAdmin } = usePermissions();
  const mayPurchase = useMayPurchaseLicense();

  const [open, setOpen] = useState(false);
  const continuation = useRef(null);
  // The gate() call in progress, if any: a ref for the guard (a second click
  // lands before React re-renders), state for the button that shows it.
  const deciding = useRef(false);
  const [pending, setPending] = useState(false);
  const cardReturnHandled = useRef(false);
  // What the reopened paywall runs after the purchase when the partner is back
  // from Stripe. A ref, so the return reads the screen's latest closure.
  const resumeAfterCard = useRef(resumeAfterCardReturn);
  resumeAfterCard.current = resumeAfterCardReturn;
  // Back from Stripe but the webhook has not delivered the method yet: the
  // modal says so instead of asking for it again.
  const [paymentMethodPending, setPaymentMethodPending] = useState(false);

  // Only an explicit `false`: an entitlements payload that has not loaded, or
  // one from a backend without the field, must not lock the purchase.
  const requiresPaymentMethod =
    paywallActive &&
    !isSuperAdmin &&
    entitlements?.payment_method_on_file === false &&
    (paymentMethodToConfirm || !holdsLicense(entitlements, productKey));

  // The paywall view on screen (or handed off and still deciding), for analytics.
  const view = useRef(null);
  // Bumped when a page restored from the back-forward cache starts a new view, so
  // the effect below counts it as it would a fresh open.
  const [restoredViews, setRestoredViews] = useState(0);

  const endView = useCallback(() => {
    view.current?.end();
    view.current = null;
  }, []);

  const close = useCallback(() => {
    continuation.current = null;
    setOpen(false);
    endView();
  }, [endView]);

  const handOffPaywall = useCallback(() => {
    continuation.current = null;
    setOpen(false);
  }, []);

  const reportPaywallSubscribed = useCallback(() => {
    view.current?.settle(PaywallOutcome.Subscribed);
  }, []);

  const reportPaywallRefusal = useCallback((refusal) => {
    view.current?.block(paywallBlocker(refusal));
  }, []);

  const openFor = useCallback(
    (proceed) => {
      if (!mayPurchase) {
        return;
      }

      continuation.current = proceed;
      // A view handed off earlier ends here: the partner is back at the paywall.
      endView();
      view.current = paywallGate ? createPaywallView(paywallGate) : null;
      // Only the return from Stripe opens in the pending state, and it sets it
      // after this call; any other open is a fresh ask.
      setPaymentMethodPending(false);
      setOpen(true);
    },
    [mayPurchase, paywallGate, endView],
  );

  // Shown means offered: counted once the entitlements say what the modal is
  // asking for. A plain confirmation of a licence already held is not a paywall;
  // the payment-method step appearing is the no-payment-method block.
  useEffect(() => {
    const current = view.current;

    if (!open || !current || !entitlements) {
      return;
    }

    if (!current.shown && holdsLicense(entitlements, productKey) && !requiresPaymentMethod) {
      return;
    }

    current.show();

    if (requiresPaymentMethod) {
      current.block(PaywallOutcome.BlockedNoPaymentMethod);
    }
  }, [open, entitlements, productKey, requiresPaymentMethod, restoredViews]);

  // A page put in the back-forward cache ended its view on `pagehide` (and one left
  // for Stripe ended it before going), but comes back with the modal still open —
  // Browser-Back from Checkout included. The restored modal is a new view: without
  // one, a later subscribe or close would report nothing.
  useEffect(() => {
    if (!open || !paywallGate) {
      return undefined;
    }

    const restoreView = (event) => {
      if (!event.persisted || (view.current && !view.current.settled)) {
        return;
      }

      view.current = createPaywallView(paywallGate);
      setRestoredViews((count) => count + 1);
    };

    window.addEventListener('pageshow', restoreView);

    return () => window.removeEventListener('pageshow', restoreView);
  }, [open, paywallGate]);

  // Leaving the screen with the paywall open, or handed off and undecided, is
  // closing it without subscribing. Leaving the page (tab closed, reload) runs no
  // cleanup; the view ends itself on `pagehide` (createPaywallView).
  useEffect(() => endView, [endView]);

  /**
   * Say whom to ask when the backend refused a login that may not buy.
   * Returns whether the refusal was one of those, so a screen can skip its
   * own generic failure toast.
   */
  const explainRefusal = useCallback(
    (error) => {
      // The API client has already toasted the partner's contact.
      if (isProviderContactRequired(error)) {
        return true;
      }

      if (!mayPurchase && error?.error_code === LicenseErrorCode.LicenseRequired) {
        toast.error(t('contact_provider'), { toastId: PROVIDER_CONTACT_TOAST_ID });

        return true;
      }

      return false;
    },
    [mayPurchase, t],
  );

  /**
   * The backend has just refused `proceed` for want of a licence — the
   * entitlements this screen read have gone stale (a licence cancelled from
   * the Partners surface in the meantime). Re-read them and open the paywall
   * holding the action, so the partner is sold the plan instead of shown a
   * generic failure.
   */
  const requirePurchase = useCallback(
    (proceed) => {
      refresh();
      openFor(proceed);
    },
    [refresh, openFor],
  );

  /**
   * Send the partner to Stripe Checkout (setup mode) to save a card. Both
   * return URLs are the screen they are on and carry the product, so the
   * matching gate reopens the paywall on the way back: at the card-on-file step
   * after a success, at the offer after a cancel.
   */
  const addPaymentMethod = useCallback(async () => {
    setPaymentMethodPending(false);

    const url = new URL(window.location.href);
    url.searchParams.delete(PAYWALL_CARD_CANCELLED_PARAM);
    url.searchParams.set(PAYWALL_CARD_RETURN_PARAM, productKey);
    const successUrl = url.toString();
    url.searchParams.set(PAYWALL_CARD_CANCELLED_PARAM, '1');

    const { data, success } = await createCardCheckout({
      success_url: successUrl,
      cancel_url: url.toString(),
    });

    if (!success || !data?.url) {
      return { ok: false, error: data };
    }

    // Leaving for Stripe ends this view: the paywall stopped at the payment
    // method. The return reopens it as a new one. The page is about to go away,
    // so the outcome goes out by beacon rather than in the next batch.
    view.current?.settle(PaywallOutcome.BlockedNoPaymentMethod, { unloading: true });
    view.current = null;
    window.location.assign(data.url);

    return { ok: true };
  }, [createCardCheckout, productKey]);

  // Back from Stripe: drop the markers from the URL so a reload does not
  // reopen the modal, and reopen the paywall — after a cancel straight at the
  // offer, after a success once the card shows up (or polling gives up). The
  // action the partner was taking did not survive the redirect: the reopened
  // modal holds what the screen named in `resumeAfterCardReturn`, or nothing —
  // the purchase itself still completes, and Go Live injects its own launch as
  // the subscribe step.
  useEffect(() => {
    if (!paywallActive || !companyId || cardReturnHandled.current) {
      return undefined;
    }

    const params = new URLSearchParams(window.location.search);

    if (params.get(PAYWALL_CARD_RETURN_PARAM) !== productKey) {
      return undefined;
    }

    cardReturnHandled.current = true;

    const clearMarkers = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete(PAYWALL_CARD_RETURN_PARAM);
      url.searchParams.delete(PAYWALL_CARD_CANCELLED_PARAM);
      window.history.replaceState(window.history.state, '', url.toString());
    };

    // Nothing was saved, so there is no card to wait for: the partner is back
    // where they left, one step before Stripe.
    if (params.has(PAYWALL_CARD_CANCELLED_PARAM)) {
      clearMarkers();
      openFor(resumeAfterCard.current);

      return undefined;
    }

    let attempts = 0;
    let timer = null;
    let cancelled = false;

    const poll = async () => {
      attempts += 1;
      const standing = await refresh();

      if (cancelled) {
        return;
      }

      if (!standing?.payment_method_on_file && attempts < CARD_POLL_ATTEMPTS) {
        timer = setTimeout(poll, CARD_POLL_INTERVAL_MS);

        return;
      }

      clearMarkers();

      openFor(resumeAfterCard.current);
      setPaymentMethodPending(!standing?.payment_method_on_file);
    };

    poll();

    return () => {
      cancelled = true;

      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [paywallActive, companyId, productKey, refresh, openFor]);

  /**
   * Run `proceed` now when no purchase stands in the way, otherwise open the
   * paywall and run `onPurchase` (by default the same action) after a
   * successful purchase. `always` opens the modal even for a licensed
   * restaurant (the already-licensed confirmation).
   *
   * `onPurchase` exists for the screen whose two outcomes differ: the
   * auto-replies toggle only puts the switch back when the licence was there
   * all along, but switches on AND saves when a purchase paid for it. Deciding
   * that inside one continuation would mean re-reading the entitlements from
   * the render the click happened in, which is exactly the frame that did not
   * have them yet.
   */
  const gate = useCallback(
    async (proceed, { always = false, onPurchase = proceed } = {}) => {
      if (deciding.current) {
        return undefined;
      }

      deciding.current = true;
      setPending(true);

      try {
        // The predicate fails closed while the entitlements are unknown, which is
        // right for a synchronous answer but wrong here: a licensed restaurant
        // would meet the paywall in its spinner state and then be asked to
        // "Confirm" something it already holds, and a restaurant the backend
        // does not bill (the partner's test restaurant) would meet it at all —
        // on `always` paths too, which open the modal whatever is held. An
        // answer is one await away, so wait for it rather than guess. A failed
        // read leaves `entitlements` null and `error` set, and the fail-closed
        // path still applies.
        let standing = entitlements;

        if (mayPurchase && paywallActive && standing === null && !error) {
          standing = await refresh();
        }

        // `paywallActive` was read in the frame the click happened in, when the
        // entitlements may not have been there yet: the read above may have
        // brought back `billing_enabled: false` (the partner's test restaurant
        // picked without being selected), and that restaurant is never sold to.
        const sellable = paywallActive && standing?.billing_enabled !== false;

        // Never for a login that may not buy: the action runs as it is, and the
        // backend's refusal names whom to ask.
        const mustOpen = mayPurchase && sellable && (always || !holdsLicense(standing, productKey));

        if (!mustOpen) {
          const outcome = await proceed();

          if (outcome?.ok === false && explainRefusal(outcome.error)) {
            return outcome;
          }

          if (sellable && isLicenseRequired(outcome)) {
            requirePurchase(onPurchase);
          }

          return outcome;
        }

        openFor(onPurchase);

        return undefined;
      } finally {
        deciding.current = false;
        setPending(false);
      }
    },
    [mayPurchase, paywallActive, entitlements, error, refresh, requirePurchase, explainRefusal, productKey, openFor],
  );

  /**
   * The default purchase: buy (unless the product is already held — Go Live
   * and a stale-payload confirmation reach here licensed), then finish what
   * the partner was doing. The held action is released only once it has run
   * without failing. `purchased` tells the modal a licence was bought in this
   * sitting, so a retry that finally runs the action is celebrated as the
   * purchase it completes — not treated as a confirmation of something the
   * restaurant held all along.
   */
  const subscribe = useCallback(
    async (key) => {
      let purchased = false;

      if (!holdsLicense(entitlements, key)) {
        const result = await activate(key, { gate: paywallGate });

        if (!result.ok) {
          view.current?.block(paywallBlocker({ error: result.error }));

          // The card was removed since this screen read the entitlements:
          // re-read them so the modal swaps Subscribe for the add-card step.
          if (result.error?.error_code === LicenseErrorCode.PaymentMethodRequired) {
            refresh();
          }

          return result;
        }

        purchased = true;
        // Bought: that is the paywall's outcome, whatever the action does next.
        view.current?.settle(PaywallOutcome.Subscribed);
      }

      const proceed = continuation.current;
      const outcome = proceed ? await proceed() : undefined;

      if (outcome?.ok === false) {
        view.current?.block(paywallBlocker({ error: outcome.error }));

        return purchased ? { ...outcome, purchased } : outcome;
      }

      continuation.current = null;
      view.current?.settle(PaywallOutcome.Subscribed);

      return { ok: true, purchased };
    },
    [entitlements, activate, refresh, paywallGate],
  );

  return useMemo(
    () => ({
      ...licensing,
      open,
      pending,
      mayPurchase,
      gate,
      requirePurchase,
      explainRefusal,
      close,
      handOffPaywall,
      reportPaywallSubscribed,
      reportPaywallRefusal,
      subscribe,
      requiresPaymentMethod,
      paymentMethodPending,
      addPaymentMethod,
      modalProps: {
        open,
        productKey,
        company,
        entitlements,
        error,
        onRetry: refresh,
        onBack: close,
        onDone: close,
        onSubscribe: subscribe,
        requiresPaymentMethod,
        paymentMethodPending,
        onAddPaymentMethod: addPaymentMethod,
        onCheckPaymentMethod: refresh,
      },
    }),
    [
      licensing,
      open,
      pending,
      mayPurchase,
      gate,
      requirePurchase,
      explainRefusal,
      close,
      handOffPaywall,
      reportPaywallSubscribed,
      reportPaywallRefusal,
      subscribe,
      requiresPaymentMethod,
      paymentMethodPending,
      addPaymentMethod,
      productKey,
      company,
      entitlements,
      error,
      refresh,
    ],
  );
}

function isLicenseRequired(outcome) {
  return outcome?.ok === false && outcome.error?.error_code === LicenseErrorCode.LicenseRequired;
}
